<script>
import ScrollToTop from "@core/components/scroll-to-top/ScrollToTop.vue"
import { $themeConfig } from "@themeConfig"
import useAutoLogout from "@/composables/useAutoLogout"
import useStore from "@/composables/useStore"
import AnnouncementService from "@/services/info/announcement.service"

export default {
  name: "App",
  components: {
    ScrollToTop,
    // Vue 3: Use defineAsyncComponent for async component imports
    LayoutVertical: defineAsyncComponent(
      () => import("@core/layouts/layout-vertical/LayoutVertical.vue"),
    ),
    LayoutHorizontal: defineAsyncComponent(
      () => import("@core/layouts/layout-horizontal/LayoutHorizontal.vue"),
    ),
    LayoutFull: defineAsyncComponent(
      () => import("@core/layouts/layout-full/LayoutFull.vue"),
    ),
    AppCustomizer: defineAsyncComponent(
      () => import("@core/layouts/components/app-customizer/AppCustomizer.vue"),
    ),
  },
  setup() {
    const instance = getCurrentInstance()
    const { proxy } = instance

    // Store composable
    const {
      skin,
      layoutType,
      announcements,
      updateWindowWidth,
      setAnnouncements,
    } = useStore()

    useAutoLogout(() => proxy.$logout())

    // Reactive state
    const enableScrollToTop = ref(
      $themeConfig?.layout?.enableScrollToTop ?? true,
    )
    const currentHash = ref("{{POST_BUILD_ENTERS_HASH_HERE}}")
    const hashChanged = ref(false)
    const newHash = ref("")
    const announcementTimer = ref(null)

    // Computed properties
    const skinClasses = computed(() => {
      const skinValue = skin.value
      if (skinValue === "bordered")
        return "bordered-layout"
      if (skinValue === "semi-dark")
        return "semi-dark-layout"
      if (skinValue === "dark")
        return "backImagedark"
      if (skinValue === "light")
        return "backImage"
      return null
    })

    const layout = computed(() => {
      const route = proxy.$route
      if (route.meta.layout === "full")
        return "LayoutFull"
      return layoutType.value === "horizontal"
        ? "LayoutHorizontal"
        : "LayoutVertical"
    })

    // Sozlamalar paneli faqat ilova qobig'ida kerak. LayoutFull — login va
    // xato sahifalari, u yerda panel chiqmasligi kerak.
    const showCustomizer = computed(
      () =>
        ($themeConfig?.layout?.customizer ?? false)
        && layout.value !== "LayoutFull",
    )

    const showAnnouncements = computed(() => {
      return proxy.$route.path !== "/login" && announcements.value.length > 0
    })

    const isDevelopment = computed(() => {
      return proxy.$root?.env === "development" || import.meta.env.DEV
    })

    // Methods
    const handleWindowResize = () => {
      updateWindowWidth(window.innerWidth)
    }

    const initializeTheme = () => {
      // Set direction
      const isRTL = $themeConfig?.layout?.isRTL
      document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr")

      // Apply dark mode
      const skinValue = skin.value
      if (skinValue === "dark") {
        document.body.classList.add("dark-layout")
      } else {
        document.body.classList.remove("dark-layout")
      }
    }

    let scheduleNextAnnouncementFetch
    const fetchAnnouncements = async () => {
      const accessToken = localStorage.getItem("access_token")
      if (!accessToken)
        return

      try {
        const response = await AnnouncementService.GetNew()
        if (response.status === 200 && response.data) {
          localStorage.setItem("announcements", JSON.stringify(response.data))
          setAnnouncements(response.data)
        }
      } catch (err) {
        console.warn("Failed to fetch announcements:", err)
      } finally {
        scheduleNextAnnouncementFetch()
      }
    }

    scheduleNextAnnouncementFetch = () => {
      if (announcementTimer.value) {
        clearTimeout(announcementTimer.value)
      }
      announcementTimer.value = setTimeout(() => fetchAnnouncements(), 30000)
    }

    const startAnnouncementPolling = () => {
      fetchAnnouncements()
    }

    const stopAnnouncementPolling = () => {
      if (announcementTimer.value) {
        clearTimeout(announcementTimer.value)
        announcementTimer.value = null
      }
    }

    const reloadApp = () => {
      currentHash.value = newHash.value
      window.location.reload()
    }

    const closeModal = () => {
      hashChanged.value = false
    }

    // Lifecycle hooks
    onMounted(() => {
      initializeTheme()
      startAnnouncementPolling()
      handleWindowResize()
      window.addEventListener("resize", handleWindowResize)
    })

    onBeforeUnmount(() => {
      window.removeEventListener("resize", handleWindowResize)
      stopAnnouncementPolling()
    })

    return {
      enableScrollToTop,
      showCustomizer,
      currentHash,
      hashChanged,
      newHash,
      skinClasses,
      layout,
      announcements,
      showAnnouncements,
      isDevelopment,
      reloadApp,
      closeModal,
    }
  },
}
</script>

<template>
  <div
    id="app"
    class="h-100"
    :class="[
      skinClasses,
      { 'has-announcement': showAnnouncements },
    ]"
  >
    <component :is="layout">
      <RouterView />

      <template #customizer>
        <AppCustomizer v-if="showCustomizer" />
      </template>
    </component>

    <!-- Update Notification Modal -->
    <div
      v-if="hashChanged && !isDevelopment"
      class="refresh-container"
    >
      <div class="notification-header">
        <button
          type="button"
          class="close-refresh-modal"
          aria-label="Close"
          @click="closeModal"
        >
          <span aria-hidden="true">
            <i class="fal fa-times fa-sm" />
          </span>
        </button>
      </div>

      <div class="notification-body">
        <div class="notification-button">
          <p class="text-center font12">
            An update is available. Please save all current work and click
            update below. You can also accept these updates by refreshing your
            browser at any time.
          </p>
          <p class="text-center">
            <span class="font10">Not updating may result in errors.</span>
          </p>
        </div>

        <div class="refresh-button text-center">
          <button
            class="btn btn-default"
            @click="reloadApp"
          >
            Update
          </button>
        </div>
      </div>
    </div>

    <!-- Scroll to Top Button -->
    <ScrollToTop v-if="enableScrollToTop" />

    <!-- Announcements Banner -->
    <div
      v-if="showAnnouncements"
      class="announcement-banner"
    >
      <div class="announcement-banner__content">
        <span class="announcement-banner__text">
          <span class="announcement-banner__track">
            <svg
              class="announcement-banner__icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
              />
              <line
                x1="12"
                y1="9"
                x2="12"
                y2="13"
              />
              <line
                x1="12"
                y1="17"
                x2="12.01"
                y2="17"
              />
            </svg>
            <span
              v-for="(item, index) in announcements"
              :key="index"
            >{{ item
            }}<span
              v-if="index < announcements.length - 1"
              class="announcement-banner__separator"
            >
              |
            </span></span>
          </span>
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Announcement Banner - Slim top bar */
.announcement-banner {
  position: fixed;
  z-index: 9999;
  top: 0;
  left: 0;
  right: 0;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  background-color: #fef3cd;
  border-bottom: 1px solid #ffc107;
  color: #856404;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
}

.announcement-banner__content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.announcement-banner__icon {
  width: 16px;
  height: 16px;
  margin-right: 8px;
  vertical-align: -3px;
  color: #d97706;
}

.announcement-banner__text {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
}

.announcement-banner__track {
  display: inline-block;
  padding-left: 100%;
  animation: announcement-marquee 25s linear infinite;
}

.announcement-banner__text:hover .announcement-banner__track {
  animation-play-state: paused;
}

@keyframes announcement-marquee {
  to {
    transform: translateX(-100%);
  }
}

.announcement-banner__separator {
  color: #c29a1f;
}

/* Update Notification Modal */
.refresh-container {
  width: 15%;
  min-width: 300px;
  position: fixed;
  bottom: 10px;
  right: 15px;
  background-color: #fff;
  padding: 25px;
  border-radius: 8px;
  box-shadow:
    0 1px 4px rgba(0, 0, 0, 0.3),
    0 0 40px rgba(0, 0, 0, 0.05);
  z-index: 1000;
}

.notification-header {
  position: relative;
  margin-bottom: 15px;
}

.close-refresh-modal {
  position: absolute;
  right: -15px;
  top: -15px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 5px;
  transition: opacity 0.2s;
}

.close-refresh-modal:hover {
  opacity: 0.7;
}

.notification-body {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.notification-button {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.refresh-button button {
  min-width: 100px;
  padding: 8px 16px;
}
</style>

<style>
/* Push layout down when announcement banner is visible */
.has-announcement .vertical-layout {
  height: calc(100vh - 32px);
  margin-top: 32px;
}

/* Global styles */
.backImage {
  position: relative;
}

/* Turon logotipi orqa fonda. ::before ishlatiladi, chunki opacity butun
   konteynerga emas, faqat fon rasmiga tegishi kerak. */
.backImage::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image: url("/turonbank.svg"); /* design-ok */
  background-size: 700px auto;
  background-position: bottom 84px right 42px;
  background-repeat: no-repeat;
  background-attachment: fixed;
  opacity: 0.2;
  pointer-events: none;
  z-index: -1;
}

/* Vue Select overrides */
.vs--single .vs__selected {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: calc(100% - 20px);
}

.vs--single .vs__selected-options {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
}
</style>
